import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { appOsToStoreMap } from "app/pages/sales-intelligence/modules/apps-search-filters/constants";
import { useCompanyName } from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCompanyName";
import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import {
    type AppsDigitalAsset,
    type DigitalAssetsList,
    type DigitalAssetType,
    digitalAssetTypeMap,
    type WebsitesDigitalAsset,
} from "app/pages/sales-intelligence/modules/company-analysis/types/digital-assets";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import solutionUrlsService from "app/pages/sales-intelligence/services/solution-urls";

import AppIconWithBadge from "./AppIconWithBadge";
import DomainChip from "./DomainChip";

type Props = {
    websites: DigitalAssetsList<WebsitesDigitalAsset>;
    apps: DigitalAssetsList<AppsDigitalAsset>;
};

const MAX_VISIBLE_APPS = 2;
const MAX_VISIBLE_DOMAINS = 1;

const CompanyCardAssets = ({
    websites: { total: websiteCount, items: websiteItems },
    apps: { total: appCount, items: appItems },
}: Props) => {
    const { translate } = useTranslate();

    const companyName = useCompanyName();

    if (websiteCount === 0 && appCount === 0) {
        return null;
    }

    const domain = websiteItems[0]?.domain;
    const hasMoreWebsites = websiteCount > MAX_VISIBLE_DOMAINS;

    const visibleApps = appItems.slice(0, MAX_VISIBLE_APPS);
    const hasMoreApps = appCount > MAX_VISIBLE_APPS;

    const handleMoreClick = (assetType: DigitalAssetType): void => {
        companyAnalysisTrackingService.widgets.trackCompanyCardAssetsMoreClick(companyName, assetType);

        void siNavigator.goToCompanyDigitalAssetsPage(domain);
    };

    const handleAppClick = (appDigitalAsset: AppsDigitalAsset) => {
        companyAnalysisTrackingService.widgets.trackDigitalAssetsAppClick(companyName, appDigitalAsset.market);
    };

    return (
        <Flex
            gap="72px"
            data-automation="company-card-assets"
        >
            {websiteCount > 0 ? (
                <Stack gap="4px">
                    <Text
                        fontVariant="caption"
                        color={colorsPalettes.carbon[250]}
                    >
                        {`${translate("si.company-analysis.company-card.assets.websites")}:`}
                    </Text>

                    <Flex gap="4px">
                        <DomainChip domain={domain} />

                        {hasMoreWebsites ? (
                            <Button
                                type="flatDark"
                                onClick={() => handleMoreClick(digitalAssetTypeMap.websites)}
                                data-automation="company-card-websites-more"
                            >
                                {`+${websiteCount - MAX_VISIBLE_DOMAINS} ${translate("si.company-analysis.company-card.assets.more")}`}
                            </Button>
                        ) : null}
                    </Flex>
                </Stack>
            ) : null}

            {appCount > 0 ? (
                <Stack gap="4px">
                    <Text
                        fontVariant="caption"
                        color={colorsPalettes.carbon[250]}
                    >
                        {`${translate("si.company-analysis.company-card.assets.apps")}:`}
                    </Text>

                    <Flex gap="4px">
                        <Flex gap="8px">
                            {visibleApps.map((app) => (
                                <AppIconWithBadge
                                    key={app.appId}
                                    icon={app.icon}
                                    market={app.market}
                                    href={solutionUrlsService.getAppAnalysisUrl(app.appId, appOsToStoreMap[app.market])}
                                    onClick={() => handleAppClick(app)}
                                />
                            ))}
                        </Flex>

                        {hasMoreApps ? (
                            <Button
                                type="flatDark"
                                onClick={() => handleMoreClick(digitalAssetTypeMap.apps)}
                                data-automation="company-card-apps-more"
                            >
                                {`+${appCount - MAX_VISIBLE_APPS} ${translate("si.company-analysis.company-card.assets.more")}`}
                            </Button>
                        ) : null}
                    </Flex>
                </Stack>
            ) : null}
        </Flex>
    );
};

export default CompanyCardAssets;
